import React from "react";
import { Typography } from "@components";
import * as styles from "./TrustedBy.module.scss";
import BackgroundFadeContainer from "@components/BackgroundFadeContainer/BackgroundFadeContainer";
import cn from "classnames";
import {
	GAIntLabtag as GAIntLabtagDark,
	Harvard as HarvardDark,
	JunoTherapeutics as JunoTherapeuticsDark,
	MerckPharma as MerckPharmaDark,
	Nautilus as NautilusDark,
	StanfordMedicine as StanfordMedicineDark,
	ThermoFisher as ThermoFisherDark,
	UCBerkeley as UCBerkeleyDark,
	AztraZeneca as AztraZenecaDark,
	BristolMyersSquibb as BristolMyersSquibbDark,
	Eurofins as EurofinsDark,
	MIT as MITDark,
} from "@assets/institutions/dark";
import {
	GAIntLabtag as GAIntLabtagLight,
	Harvard as HarvardLight,
	JunoTherapeutics as JunoTherapeuticsLight,
	MerckPharma as MerckPharmaLight,
	Nautilus as NautilusLight,
	StanfordMedicine as StanfordMedicineLight,
	ThermoFisher as ThermoFisherLight,
	UCBerkeley as UCBerkeleyLight,
	AztraZeneca as AztraZenecaLight,
	BristolMyersSquibb as BristolMyersSquibbLight,
	Eurofins as EurofinsLight,
	MIT as MITLight,
} from "@assets/institutions/light";
import HIGH_PERFORMER_FALL from "./assets/high-performer-fall.png";
import HIGH_PERFORMER_SPRING from "./assets/high-performer-spring.png";
import { StaticImage } from "gatsby-plugin-image";
import classNames from "classnames";

type TrustedByProps = {
	/** background type of this componente (default="blueFade") */
	backgroundType?: "blueFade" | "pinkFade" | "solidLightPink";
	/** whether to hide bottom skew */
	noBottomSkew?: boolean;
};

export default function TrustedBy({
	backgroundType = "blueFade",
	noBottomSkew = false,
}: TrustedByProps) {
	const lightmode = backgroundType !== "blueFade";
	const PARTNER_IMG_LIST = lightmode
		? [
				[
					<div className={styles.astrazeneca}>
						<AztraZenecaDark />
					</div>,
					<JunoTherapeuticsDark />,
					<UCBerkeleyDark />,
					<ThermoFisherDark />,
					<HarvardDark />,
				],
				[
					<div className={styles.bms}>
						<BristolMyersSquibbDark />
					</div>,
					<StanfordMedicineDark />,
					<MerckPharmaDark />,
					<EurofinsDark />,
					<MITDark />,
				],
		  ]
		: [
				[
					<div className={styles.astrazeneca}>
						<AztraZenecaLight />
					</div>,
					<JunoTherapeuticsLight />,
					<UCBerkeleyLight />,
					<ThermoFisherLight />,
					<HarvardLight />,
				],
				[
					<div className={styles.bms}>
						<BristolMyersSquibbLight />
					</div>,
					<StanfordMedicineLight />,
					<MerckPharmaLight />,
					<EurofinsLight />,
					<MITLight />,
				],
		  ];

	const renderPartnerList = () => {
		return PARTNER_IMG_LIST.map((rows, i) => (
			<div
				className={classNames(styles.institutions, {
					[styles.secondRow]: i === 1,
				})}
				key={i}
			>
				{rows.map((PartnerImg, index) => (
					<div key={index} className={styles.institutionContainer}>
						{PartnerImg}
					</div>
				))}
			</div>
		));
	};
	return (
		<BackgroundFadeContainer
			type={backgroundType}
			skewTop
			skewBottom={!noBottomSkew}
		>
			<div
				className={cn(styles.trustedByWrapper, {
					[styles.trustedByWrapper__noBottom]: noBottomSkew,
				})}
			>
				<Typography
					center
					variant="HEADER1"
					className={styles.title}
					color={lightmode ? "marine" : "ui-01"}
				>
					See why hundreds of institutions trust Genemod
				</Typography>
				<HighPerformer lightmode={lightmode} />
				{renderPartnerList()}
			</div>
		</BackgroundFadeContainer>
	);
}

export const HighPerformer = ({lightmode = false}) => (
	<div className={styles.highPerformer}>
		<StaticImage
			loading="eager"
			height={83}
			width={73.51}
			src="./assets/high-performer-summer.png"
			alt="High Performer Summer"
		/>
		<div className={styles.score}>
			<Typography
				variant="HEADER2"
				color={lightmode ? "marine" : "ui-01"}
			>
				4.7/5
			</Typography>
			<div className={styles.stars}>
				<Star/>
				<Star/>
				<Star/>
				<Star/>
				<HalfStar/>
			</div>
		</div>
		<StaticImage
			loading="eager"
			height={83}
			width={73.51}
			src="./assets/momentum-leader-summer.png"
			alt="Momentum Leader Summer"
		/>
	</div>
)

const Star = () => {
	return (
		<div style={{height: 28, width: 28}}>
			<svg
				width="24"
				height="24"
				viewBox="0 0 24 22"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M11.0554 0.717389C11.3667 -0.178171 12.6333 -0.178173 12.9446 0.717387L15.0631 6.81222C15.2005 7.20729 15.5691 7.47516 15.9873 7.48368L22.4385 7.61515C23.3864 7.63447 23.7778 8.83901 23.0223 9.41181L17.8804 13.3101C17.5471 13.5628 17.4063 13.9962 17.5274 14.3965L19.3959 20.5726C19.6705 21.4801 18.6458 22.2246 17.8676 21.683L12.5712 17.9975C12.2279 17.7586 11.7721 17.7586 11.4288 17.9975L6.13241 21.683C5.35417 22.2246 4.32953 21.4801 4.60408 20.5726L6.47258 14.3965C6.5937 13.9962 6.45287 13.5628 6.11957 13.3101L0.977713 9.41181C0.222181 8.83901 0.613559 7.63447 1.56148 7.61515L8.01268 7.48368C8.43086 7.47516 8.79955 7.20729 8.93687 6.81222L11.0554 0.717389Z"
					fill="#FF492C"
				/>
			</svg>
		</div>
	);
};

const HalfStar = () => {
	return (
		<svg
			width="28"
			height="28"
			viewBox="0 0 28 28"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M14.4723 2.88155L16.5908 8.97638C16.7968 9.56899 17.3499 9.9708 17.9771 9.98358L24.4283 10.115C24.9023 10.1247 25.098 10.727 24.7202 11.0134L19.5784 14.9117C19.0784 15.2907 18.8672 15.9408 19.0488 16.5413L20.9173 22.7174C21.0546 23.1712 20.5423 23.5434 20.1532 23.2726L14.8568 19.587C14.3418 19.2287 13.6582 19.2287 13.1432 19.587L7.84683 23.2726C7.4577 23.5434 6.94538 23.1712 7.08266 22.7174L8.95116 16.5413C9.13283 15.9408 8.92159 15.2907 8.42164 14.9117L3.27979 11.0134C2.90202 10.727 3.09771 10.1247 3.57167 10.115L10.0229 9.98358C10.6501 9.9708 11.2032 9.56899 11.4092 8.97639L13.5277 2.88155C13.6834 2.43377 14.3166 2.43377 14.4723 2.88155Z"
				stroke="#FF492C"
			/>
			<path
				d="M17.5 21V10L14 3L11 10H3.5V11L9.5 16L7.5 23L14 19L17.5 21Z"
				fill="#FF492C"
			/>
		</svg>
	);
};
